import { fetchDivisions, fetchStandingsByDivision } from '../divisions/api.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import type { Division } from '@multirx/shared/types/division.js';
import type { DivisionStandingsView } from '@multirx/shared/types/standings.js';
import { createPageShell } from '../ui/pageShell.js';
import { createPageEmptyState } from '../ui/pageEmptyState.js';
import { replaceAppHash } from '../ui/appHash.js';
import { el } from '../ui/dom.js';
import { readStoredStandingsDivision, storeStandingsDivision } from './standingsDivisionPrefs.js';
import { bindStandingsDriverTooltips } from './standingsDriverTooltip.js';
import {
  bindStandingsPointsBreakdownTooltips,
  invalidateStandingsPointsBreakdownCache,
} from './standingsPointsBreakdownTooltip.js';
import {
  standingsPointsCellHtml,
  standingsRankCellHtml,
  standingsRowClass,
  standingsPointsColumnsHeadHtml,
  standingsPointsColumnsHtml,
} from './standingsTableMarkup.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { createEarthLoader } from '../ui/earthLoader.js';
import { shouldApplyStandingsResult, shouldStartStandingsFetch } from './standingsLoadGuard.js';

export type StandingsMountOptions = {
  embedded?: boolean;
  /** Intégré sous la saison en cours : pas d’intro redondante. */
  compact?: boolean;
};

export type StandingsController = {
  selectDivision: (divisionId: number) => void;
  getSelectedDivision: () => number;
  refresh: () => void;
};

export function mountStandingsApp(
  root: HTMLElement,
  options: StandingsMountOptions = {}
): StandingsController {
  const embedded = options.embedded === true;
  const compact = options.compact === true;
  root.innerHTML = '';
  root.className = embedded
    ? `seasons-standings-embed${compact ? ' seasons-standings-embed--compact' : ''}`
    : 'app-view circuits-app';

  let selectedDivisionId = embedded
    ? readStoredStandingsDivision()
    : (() => {
        const match = window.location.hash.match(/[?&]division=(\d)/);
        const id = match ? parseInt(match[1], 10) : 1;
        return id >= 1 && id <= 4 ? id : 1;
      })();

  let body: HTMLElement;
  let subtitleEl: HTMLElement | null = null;

  if (embedded) {
    body = root;
    if (!compact) {
      const lead = el('p', {
        class: 'seasons-tab-lead',
        text: 'Points cumulés de la saison championnat en cours, par division.',
      });
      body.appendChild(lead);
      subtitleEl = el('p', { class: 'seasons-tab-meta page-subtitle' });
      body.appendChild(subtitleEl);
    }
  } else {
    const { page, body: shellBody } = createPageShell({
      title: 'Classement',
      subtitle: 'Points cumulés de la saison championnat en cours, par division.',
      breadcrumbs: [{ label: 'Saisons', href: '#saisons' }, { label: 'Classement' }],
    });
    body = shellBody;
    subtitleEl = page.querySelector('.page-subtitle');
    root.appendChild(page);
  }

  const tabsContainer = el('div', {
    class: embedded ? 'seasons-standings-div-tabs' : 'tabs',
    role: 'tablist',
  });
  const content = el('div', { class: embedded ? 'seasons-standings-content' : 'page-body' });
  body.appendChild(tabsContainer);
  body.appendChild(content);
  bindStandingsDriverTooltips(content);
  bindStandingsPointsBreakdownTooltips(content);

  let divisions: Division[] = [];
  /** Cache session de page : un fetch réussi par division tant que l’app est montée. */
  const viewCache = new Map<number, DivisionStandingsView>();
  const inFlight = new Map<number, Promise<DivisionStandingsView>>();

  function renderTabs(): void {
    tabsContainer.innerHTML = '';
    divisions.forEach((div) => {
      const tab = el('button', {
        type: 'button',
        class: embedded
          ? `seasons-standings-div-tab${div.id === selectedDivisionId ? ' is-active' : ''}`
          : `tab${div.id === selectedDivisionId ? ' is-active' : ''}`,
        text: `Division ${div.id}`,
      });
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', div.id === selectedDivisionId ? 'true' : 'false');
      tab.addEventListener('click', () => {
        selectDivision(div.id);
      });
      tabsContainer.appendChild(tab);
    });
  }

  function showEarthLoader(): void {
    content.innerHTML = '';
    const wrap = el('div', { class: 'seasons-standings-loader' });
    wrap.appendChild(createEarthLoader('Chargement du classement…'));
    content.appendChild(wrap);
  }

  function renderNoSeasonState(): void {
    content.innerHTML = '';
    const block = createPageEmptyState({
      title: 'Aucune saison en cours',
      description:
        'Le classement n’affiche que les points des saisons championnat (meetings, qualifs, finales). Les courses libres ne comptent pas.',
      primaryAction: { label: 'Aller aux Saisons', href: '#saisons', variant: 'cta' },
    });
    content.appendChild(block);
  }

  function renderSeasonEmptyState(view: DivisionStandingsView): void {
    content.innerHTML = '';
    const meeting =
      view.currentMeetingIndex != null && view.meetingCount != null
        ? `Meeting ${view.currentMeetingIndex} / ${view.meetingCount}. `
        : '';
    const block = createPageEmptyState({
      title: 'Aucun point pour l’instant',
      description: `${meeting}Jouez ou simulez des sessions depuis l’onglet Saisons pour remplir ce classement.`,
      primaryAction: {
        label: 'Reprendre la saison',
        href: '#saisons',
        variant: 'cta',
      },
    });
    content.appendChild(block);
  }

  function renderStandingsTable(
    entries: DivisionStandingsView['standings'],
    seasonId: number | undefined
  ): void {
    content.innerHTML = '';
    const wrap = el('div', { class: 'standings-board seasons-standings-board data-table-wrap' });
    const table = el('table', {
      class: embedded
        ? 'seasons-standings-table data-table data-table--standings'
        : 'seasons-standings-table data-table data-table--standings',
      role: 'table',
    });
    const thead = el('thead');
    thead.innerHTML = `
      <tr>
        <th scope="col" class="col-rank">Rang</th>
        <th scope="col">Pilote</th>
        <th scope="col" class="col-flag" aria-label="Pays">Pays</th>
        ${standingsPointsColumnsHeadHtml()}
      </tr>
    `;
    const tbody = el('tbody');
    entries.forEach((entry, index) => {
      const rank = index + 1;
      const tr = el('tr', { class: standingsRowClass(rank) });
      tr.dataset.driverId = String(entry.driverId);
      const rawName = `${entry.driverFirstName} ${entry.driverLastName}`;
      tr.dataset.driverName = rawName;
      tr.dataset.standingsRank = String(rank);
      tr.dataset.standingsPoints = String(entry.points);
      tr.dataset.division = String(entry.division);
      if (seasonId != null) tr.dataset.seasonId = String(seasonId);
      tr.tabIndex = 0;
      const pointsCols =
        seasonId != null
          ? standingsPointsColumnsHtml(
              entry.points,
              entry.qualPoints ?? 0,
              entry.coursePoints ?? 0,
              rank,
              { seasonId, driverId: entry.driverId }
            )
          : `<td class="col-points col-points--champ">${standingsPointsCellHtml(entry.points, rank)}</td>
        <td class="col-points col-points--qual">—</td>
        <td class="col-points col-points--course">—</td>`;
      tr.innerHTML = `
        <td class="col-rank">${standingsRankCellHtml(rank)}</td>
        <td class="col-driver">${labelOwnDriver(rawName, entry.driverId)}</td>
        <td class="col-flag">${nationalityFlagMarkup(entry.driverNationality)}</td>
        ${pointsCols}
      `;
      tbody.appendChild(tr);
    });
    table.appendChild(thead);
    table.appendChild(tbody);
    wrap.appendChild(table);
    content.appendChild(wrap);
  }

  function paintView(view: DivisionStandingsView): void {
    if (!view.hasActiveSeason) {
      if (subtitleEl) {
        subtitleEl.textContent =
          'Aucune saison championnat active — lancez une campagne depuis Saisons.';
      }
      renderNoSeasonState();
      return;
    }

    if (subtitleEl && view.currentMeetingIndex != null && view.meetingCount != null) {
      subtitleEl.textContent = `Saison en cours — meeting ${view.currentMeetingIndex} / ${view.meetingCount}. Seules les sessions championnat comptent.`;
    }

    if (view.standings.length === 0) {
      renderSeasonEmptyState(view);
      return;
    }

    renderStandingsTable(view.standings, view.seasonId);
  }

  function ensureFetch(divisionId: number): Promise<DivisionStandingsView> {
    const flyingFor = inFlight.has(divisionId) ? divisionId : null;
    if (!shouldStartStandingsFetch(divisionId, flyingFor)) {
      return inFlight.get(divisionId)!;
    }

    const request = fetchStandingsByDivision(divisionId)
      .then((view) => {
        viewCache.set(divisionId, view);
        return view;
      })
      .finally(() => {
        inFlight.delete(divisionId);
      });
    inFlight.set(divisionId, request);
    return request;
  }

  function loadStandings(divisionId: number, force = false): void {
    if (!force) {
      const cached = viewCache.get(divisionId);
      if (cached) {
        if (shouldApplyStandingsResult(divisionId, selectedDivisionId)) {
          paintView(cached);
        }
        return;
      }
    } else {
      viewCache.delete(divisionId);
      inFlight.delete(divisionId);
    }

    if (shouldApplyStandingsResult(divisionId, selectedDivisionId)) {
      showEarthLoader();
    }

    void ensureFetch(divisionId)
      .then((view) => {
        if (!shouldApplyStandingsResult(divisionId, selectedDivisionId)) return;
        paintView(view);
      })
      .catch((err) => {
        if (!shouldApplyStandingsResult(divisionId, selectedDivisionId)) return;
        if (!content.isConnected) return;
        content.innerHTML = '';
        content.appendChild(
          el('p', { class: 'empty-state', text: err instanceof Error ? err.message : 'Erreur' })
        );
      });
  }

  function selectDivision(divisionId: number): void {
    if (divisionId < 1 || divisionId > 4) return;
    if (
      divisionId === selectedDivisionId &&
      (viewCache.has(divisionId) || inFlight.has(divisionId))
    ) {
      return;
    }
    selectedDivisionId = divisionId;
    if (embedded) {
      storeStandingsDivision(divisionId);
    } else {
      replaceAppHash(`classement?division=${divisionId}`);
    }
    renderTabs();
    loadStandings(divisionId);
  }

  fetchDivisions()
    .then((divs) => {
      divisions = divs;
      renderTabs();
      loadStandings(selectedDivisionId);
    })
    .catch(() => {
      divisions = [...DIVISIONS];
      renderTabs();
      loadStandings(selectedDivisionId);
    });

  return {
    selectDivision,
    getSelectedDivision: () => selectedDivisionId,
    refresh: () => {
      invalidateStandingsPointsBreakdownCache();
      viewCache.clear();
      inFlight.clear();
      loadStandings(selectedDivisionId, true);
    },
  };
}
